import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useDebounce, useDebouncedCallback } from 'use-debounce';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { MuuriComponent, useRefresh } from 'muuri-react';
import dayjs from 'dayjs';
import reactStringReplace from 'react-string-replace';
import {
  Alert,
  Backdrop,
  CircularProgress,
  Grid,
  IconButton,
  Snackbar,
  Switch,
  Tooltip
} from '@mui/material';

import {
  FullPageLoader,
  MDBox,
  CommentsList,
  Configurator,
  FaIcon,
  FileUploadModal,
  If,
  MDTypography,
  SqBChatWidget,
  CleanActionsModal,
  InsightsModal
} from '@components';
import MainLayout from '@layouts/MainLayout';
import ChartCard from '../ChartCard';
import ProjectHeader from '../ProjectHeader';
import ReportGenerationModal from '../ReportGenerationModal';
import SelectThemeMenu from '@pages/EditChart/components/SelectThemeMenu';
import { ResizableWrapper } from '../ResizableWrapper';
import RefreshDataModal from '../RefreshDataModal';
import PBFilter from '../PBFilter';

import { ChartConfigType } from '@DStypes/chart';
import useCharts from '@hooks/useCharts';

import { RootState } from '@store/store';
import { setPlanUpdateErrors } from '@store/AppSlice';
import {
  resetSqBotData,
  setCurrentProjectData,
  setDropDownData,
  updateChartIdOrder
} from '@pages/ProjectBoard/slice';
import {
  setMiniSidenav,
  setOpenConfigurator,
  useMaterialUIController
} from '@context/MDContext';
import {
  useCreateProjectCommentMutation,
  useLazyGetProjectCommentsQuery,
  useUpdateProjectThemeMutation,
  useUpdateProjectChartOrderMutation,
  useUpdateProjectMutation,
  useLazyGetProjectByIdQuery,
  useRefreshSpreadsheetDataMutation
} from '@api/projects';
import { useLazyGetFeatureAccessQuery } from '@api/usePlans';
import { useUpdateNotificationAsReadMutation } from '@api/notifications';
import { useLazyGetChartDetailsQuery } from '@api/chart';

import { sendAmplitudeData } from '@utils/amplitude';
import { am4core, getFilterTooltipText } from '@utils/chart';
import {
  getCurrentFilter,
  getFileType,
  getPlanName,
  getUTCTime
} from '@utils/common';
import breakpoints from '@theme/base/breakpoints';
import linearGradient from '@theme/functions/linearGradient';
import useToast from '@hooks/useToast';
import useProjects from '@hooks/useProject';
import useUserDetails from '@hooks/useUserDetails';
import UserAuth from '@services/auth';
import { MSG_EVENT, NotificationData } from '@DStypes/notifications';
import {
  DATE_GROUP,
  DATE_TIME_GROUP,
  FeatureTypes,
  ProjectStatus,
  pbWithChartsId
} from '@constants/common';
import {
  GSHEET_UPDATE_INITIATED,
  INSIGHT_CREATION_FAILED,
  NO_DATA_MODIFICATIONS_AFTER_REFRESH
} from '@constants/messages';
import {
  CHART_BOARD_RENDER_SUCCESS,
  CLICKED_ADD_DATA,
  CLICKED_DOWNLOAD,
  CLICKED_REMOVE_WATERMARK,
  CLICKED_SHOW_INSIGHT,
  CONFIRM_REFRESH_DATA,
  DELETE_MULTI_CHART,
  INITIATED_CHART_REPOSITION,
  REPOSITION_CHART,
  SET_3D,
  SET_GRADIENT,
  SET_MULTI_COLOUR
} from '@constants/eventNames';

import './styles.scss';
import brandLogo from '@assets/images/DS_Logo.png';

interface Props {
  chartConfigs: ChartConfigType[];
  isBoardPreview: boolean;
}

interface ProjectBoardChartProps {
  children: ReactNode;
  width: number;
  height: number;
  configuratorFlag: boolean;
}

const ProjectBoardCharts: React.FC<ProjectBoardChartProps> = ({
  children,
  width,
  height,
  configuratorFlag
}) => {
  const refresh = useRefresh();

  useEffect(() => {
    setTimeout(() => {
      refresh();
    }, 600);
  }, [configuratorFlag]);

  return (
    <div style={{ width, height, marginRight: '8px', marginBottom: '10px' }}>
      {children}
    </div>
  );
};

const ChartBoard: React.FC<Props> = ({ chartConfigs, isBoardPreview }) => {
  const dispatch = useDispatch();
  const { projectId, collaborationId } = useParams();
  const { pushInfoToast } = useToast();
  const [controller, configuratorDispatch] = useMaterialUIController();
  const { openConfigurator, preventClosing, miniSidenav, userModifiedSidenav } =
    controller;
  const { pushSuccessToast, pushErrorToast } = useToast();

  const projectData = useSelector(
    (state: RootState) => state?.projectBoard?.currentProjectData
  );
  const boardData = useSelector(
    (state: RootState) => state?.projectBoard?.data
  );
  const subscriptionFlag = useSelector(
    (state: RootState) => state?.AppData?.subKeyFlag
  );
  const notifications = useSelector(
    (state: RootState) => state?.notificationsData
  );
  const userDetails = useSelector((state: RootState) => state?.userDetails);
  const projectBoard = useSelector((state: RootState) => state?.projectBoard);

  const projectFields = useSelector(
    (state: RootState) => state?.projectBoard?.dropDownData
  );
  const isChartInsightsEnabledByPlan = useSelector(
    (state: RootState) => state?.AppData?.currentPlanFeatures?.chartInsights
  );
  const isProjectInsightsEnabledByPlan = useSelector(
    (state: RootState) => state?.AppData?.currentPlanFeatures?.projectInsights
  );
  const isUpdateDataEnabledByPlan = useSelector(
    (state: RootState) => state?.AppData?.currentPlanFeatures?.manualUpdate
  );
  const user = UserAuth.userInfo;

  const [getProjectById] = useLazyGetProjectByIdQuery();
  const { getUserDetails } = useUserDetails();
  const [
    refreshData,
    {
      data: dataRefreshResponse,
      isSuccess: isDataRefreshSuccess,
      isError: isDataRefreshError,
      error: dataRefreshError
    }
  ] = useRefreshSpreadsheetDataMutation();
  const [updateProject, { data: updatedProjectData }] =
    useUpdateProjectMutation();
  const { getChartData, deleteMultipleCharts, isMultipleDeleteStarted } =
    useCharts({});
  const [updateTheme, { data: themeData }] = useUpdateProjectThemeMutation();
  const [updateOrderWithBackend] = useUpdateProjectChartOrderMutation();
  const [markAsRead] = useUpdateNotificationAsReadMutation();
  const [getChartDetails, { data: dropDownData }] =
    useLazyGetChartDetailsQuery();
  const [getProjectComments, { data: commentsList }] =
    useLazyGetProjectCommentsQuery();
  const [createComment] = useCreateProjectCommentMutation();
  const [getFeatureAccess] = useLazyGetFeatureAccessQuery();

  const segmentedChartIndex = chartConfigs?.findIndex(
    (chart) => chart?.config?.segment_field
  );
  const totalChartCount = chartConfigs?.length;
  const unitHeight = 140;

  const typographyRef = useRef<HTMLDivElement>(null);
  const showTooltip =
    typographyRef?.current &&
    typographyRef?.current?.getBoundingClientRect().height > 60
      ? true
      : false;

  const isInitialLoad = useRef(true);
  const parentContainerRef = useRef<HTMLDivElement | null>(null);
  const [searchText, setSearchText] = useState('');
  const [selectedChartIds, setSelectedChartIds] = useState<string[]>([]);
  const [svgGeneratedCharts, setSvgGeneratedCharts] = useState<string[]>([]);
  const [chartDataLoading, setChartDataLoading] = useState(true);
  const [selectThemeToggle, setSelectThemeToggle] = useState(false);
  const [showThemeMenu, setShowThemeMenu] = useState(false);
  const [themeTooltip, setThemeTooltip] = useState('Click to add a Theme');
  const [loadedCharts, setLoadedCharts] = useState<ChartConfigType[]>([]);
  const [chartOrderArr, setChartOrderArr] = useState<string[] | null>(null);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [mobileView, setMobileView] = useState<boolean>(false);
  const [chart3dToggle, set3dChartToggle] = useState<boolean>();
  const [gradientChartToggle, setGradientChartToggle] = useState<boolean>();
  const [multiColorChartToggle, setMultiColorChartToggle] = useState<boolean>();
  const [themeChangedAfterSvgGeneration, setThemeChangedAfterSvgGeneration] =
    useState(false);
  const [forceUpdate, setForceUpdate] = useState<any[]>([]);
  const [cleanedColumnsRedirect, setCleanedColumnsRedirect] = useState(false);
  const [isAddDataModalOpen, setIsAddDataModalOpen] = useState<boolean>();
  const [isCombineReady, setIsCombineReady] = useState<boolean>(false);
  const [isRefetchCharts, setRefetchCharts] = useState(false);
  const [isRefetchChartConfigs, setRefetchChartConfigs] = useState(false);
  const [openSqBChatWidget, setOpenSqBChatWidget] = useState(false);
  const [openInsightsModal, setOpenInsightsModal] = useState(false);
  const [openRefreshDataModal, setOpenRefreshDataModal] =
    useState<boolean>(false);
  const [infoSnackBarMessage, setInfoSnackBarMessage] = useState<
    string | ReactNode
  >('');
  const [showRedFlag, setShowRedFlag] = useState(false);
  const [pollingIntForNewChart, setpollingIntForNewChart] = useState(500);
  const [startPollingForSquirby, setStartPollingForSquirby] = useState(false);
  const [configuratorFlag, setConfiguratorFlag] = useState<number>(0);
  const [isInitiatePolling, setInitiatePolling] = useState(false);
  const [chartBoardLoader, setChartBoardLoader] = useState(false);
  const [openSuccessSnackBar, setOpenSuccessSnackBar] = useState(true);
  const [sendInitText, setSendInitText] = useState(false);
  const [loadedChartCount, setLoadedChartCount] = useState(0);
  const [chartLoadTime, setChartLoadTime] = useState<{
    renderingInitedAt: string;
    renderingStartedAt?: string;
    renderingEndedAt?: string;
  }>({ renderingInitedAt: new Date().toUTCString() });
  const [unitWidth, setUnitWidth] = useState(0);
  const [openCleanActionsModal, setOpenCleanActionsModal] = useState(false);

  const [debouncedWindowWidth] = useDebounce(window.innerWidth, 2000);

  const { getChartConfigs } = useProjects({
    pollingInterval: startPollingForSquirby ? pollingIntForNewChart : 0,
    pollingCallBack: (data: any[], charts: any[]) => {
      data?.length > charts?.length ? handlePollingEnd() : null;
    }
  });

  const sortedChartConfigs = useMemo(() => {
    const sortedArr: ChartConfigType[] = [];

    if (chartOrderArr?.[0] === 'none') return chartConfigs;

    if (chartOrderArr) {
      chartOrderArr.forEach((id) => {
        const obj = chartConfigs.find((obj) => obj.id === id);

        if (obj) sortedArr.push(obj);
      });

      return sortedArr;
    }

    return [];
  }, [chartConfigs, chartOrderArr]);

  let chartDataArr = useMemo(() => {
    if (searchText)
      return sortedChartConfigs
        .map((item: any) => {
          const data = projectBoard.data?.[item.id]?.data;
          const chartType = item?.config?.chart_type;

          if (data && data?.length) {
            const axisAndSegments =
              chartType === 'PIE' || chartType === 'KPI'
                ? [Object.keys(data?.[0])?.[0]]
                : Object.keys(data?.[0]);

            const axisAndSegmentsLowerCase = axisAndSegments.map((segment) =>
              String(segment).toLowerCase()
            ); // convert each segment to lowercase

            const xAxis = axisAndSegments[0];
            const xAxisValues =
              chartType === 'KPI' ? [] : data?.map((item: any) => item[xAxis]);

            const xAxisValuesLowerCase =
              chartType === 'KPI'
                ? []
                : xAxisValues.map((value: string) =>
                    String(value).toLowerCase()
                  );

            if (
              item?.chart_title
                ?.toLowerCase()
                .includes(searchText.toLowerCase()) ||
              item?.chart_long_desc
                ?.toLowerCase()
                .includes(searchText.toLowerCase()) ||
              item?.chart_short_desc
                ?.toLowerCase()
                .includes(searchText.toLowerCase()) ||
              item?.config?.segment_field?.fieldname
                ?.toLowerCase()
                .includes(searchText.toLowerCase()) ||
              axisAndSegmentsLowerCase.some((segment) =>
                segment.includes(searchText.toLowerCase())
              ) ||
              xAxisValuesLowerCase.some((value: string) =>
                value.includes(searchText.toLowerCase())
              )
            )
              return item;
          } else if (
            item?.chart_title
              ?.toLowerCase()
              .includes(searchText.toLowerCase()) ||
            item?.chart_long_desc
              ?.toLowerCase()
              .includes(searchText.toLowerCase()) ||
            item?.chart_short_desc
              ?.toLowerCase()
              .includes(searchText.toLowerCase()) ||
            item?.config?.segment_field?.fieldname
              ?.toLowerCase()
              .includes(searchText.toLowerCase())
          ) {
            return item;
          }
        })
        .filter((item: any) => item !== undefined);
    else return sortedChartConfigs;
  }, [sortedChartConfigs, searchText]);

  const showItem = useMemo(
    () => (chartData: ChartConfigType) =>
      (
        <ChartCard
          chartConfig={chartData}
          onCheckboxChange={() => handleCheckboxChange(chartData.id)}
          isSelected={selectedChartIds?.includes(chartData.id)}
          onDeleteChartById={() => handleDeleteChartById(chartData.id)}
          projectColorSet={projectData?.colorset}
          isBoardPreview={isBoardPreview}
          disableDrag={!!searchText || isBoardPreview}
          isGoAutoEnabled={
            chartConfigs.filter(
              (chartData: any) => chartData.created_by_auto_charts === true
            ).length > 0
              ? false
              : true
          }
          chartLoadedCallback={() => {
            setLoadedChartCount((prev) => prev + 1);
          }}
          availableFields={projectFields?.available_fields}
          onClickRemoveWaterMark={onClickRemoveWaterMark}
          subscriptionFlag={subscriptionFlag}
          isChartInsightsEnabledByPlan={isChartInsightsEnabledByPlan}
        />
      ),
    [
      chartDataArr,
      selectedChartIds,
      projectData?.colorset,
      isBoardPreview,
      projectData,
      projectFields,
      subscriptionFlag
    ]
  );

  useEffect(() => {
    const forceUpdateValues = chartConfigs.map((chartConfig) => ({
      id: chartConfig.id,
      update: false
    }));

    setForceUpdate(forceUpdateValues);
  }, [chartConfigs]);

  useEffect(() => {
    if (!isBoardPreview && projectData?.id)
      getChartDetails({
        id: projectData?.id as string
      });
  }, [isBoardPreview, projectData?.id]);

  useEffect(() => {
    if (dropDownData) dispatch(setDropDownData(dropDownData));
  }, [dropDownData]);

  useEffect(() => {
    if (startPollingForSquirby && projectData?.id)
      getChartConfigs({ projectId: projectData.id, collaboration_id: '' });
  }, [startPollingForSquirby, projectData?.id]);

  useEffect(() => {
    if (projectData?.id) getProjectComments(projectData?.id);
  }, [projectData?.id]);

  useEffect(() => {
    if (themeData || themeData === null)
      dispatch(setCurrentProjectData({ colorset: themeData }));
  }, [themeData]);

  useEffect(() => {
    if (
      loadedChartCount === totalChartCount &&
      !chartLoadTime?.renderingEndedAt
    )
      setChartLoadTime((prev) => ({
        ...prev,
        renderingEndedAt: new Date().toUTCString()
      }));
  }, [loadedChartCount]);

  useEffect(() => {
    if (
      totalChartCount === Object.keys(boardData || {})?.length &&
      !chartLoadTime?.renderingStartedAt
    )
      setChartLoadTime((prev) => ({
        ...prev,
        renderingStartedAt: new Date().toUTCString(),
        renderingEndedAt: ''
      }));
  }, [Object.keys(boardData || {})?.length]);

  useEffect(() => {
    if (chartLoadTime?.renderingEndedAt)
      sendAmplitudeData(CHART_BOARD_RENDER_SUCCESS, {
        renderingInitedAt: chartLoadTime.renderingInitedAt,
        renderingStartedAt: chartLoadTime.renderingStartedAt,
        renderingEndedAt: chartLoadTime.renderingEndedAt,
        renderingTimeInSeconds: dayjs(chartLoadTime.renderingEndedAt).diff(
          chartLoadTime.renderingStartedAt,
          'second'
        )
      });
  }, [chartLoadTime?.renderingEndedAt]);

  useEffect(() => {
    if (debouncedWindowWidth < breakpoints.values.lg) setMobileView(true);
    else setMobileView(false);
  }, [debouncedWindowWidth]);

  // hook to init theme settings values whenever projectDetails is updated
  useEffect(() => {
    if (projectData) {
      if (projectData?.colorset) setSelectThemeToggle(true);

      set3dChartToggle(projectData.visual_settings?.['3d_charts']);
      setGradientChartToggle(projectData.visual_settings?.gradient_charts);
      setMultiColorChartToggle(projectData.visual_settings?.multicolor_charts);
    }
  }, [projectData]);

  // hook to handle success/failure of filter and combine based on notification
  useEffect(() => {
    const handleFilterNotification = async () => {
      if (notifications.unreadMessages?.length) {
        const messages: NotificationData[] = [];
        const errMessages: NotificationData[] = [];

        notifications.unreadMessages.forEach((notification) => {
          const notificationCreatedAt = new Date(
            notification.created_at
          ).toUTCString();
          const timeDiff = dayjs(notificationCreatedAt).diff(
            chartLoadTime.renderingInitedAt,
            'second'
          );

          if (timeDiff > 0) {
            if (
              notification.message_event ===
                MSG_EVENT.DASHBOARD_FILTER_SUCCESS ||
              notification.message_event ===
                MSG_EVENT.DASHBOARD_FILTER_REMOVED ||
              notification.message_event === MSG_EVENT.FILE_COMBINE_SUCCESS ||
              notification.message_event ===
                MSG_EVENT.DATA_RECALCULATION_COMPLETED ||
              notification.message_event === MSG_EVENT.CLOUD_UPDATE_COMPLETED
            )
              messages.push(notification);
            if (
              notification.message_event ===
                MSG_EVENT.DASHBOARD_FILTER_FAILED ||
              notification.message_event ===
                MSG_EVENT.FILE_COMBINE_UNSUCCESSFUL ||
              notification.message_event === MSG_EVENT.FILE_COMBINE_FAIL
            ) {
              errMessages.push(notification);
              if (
                notification.message_event ===
                  MSG_EVENT.FILE_COMBINE_UNSUCCESSFUL ||
                notification.message_event === MSG_EVENT.FILE_COMBINE_FAIL
              )
                setChartBoardLoader(false);
            }
          }
        });

        if (errMessages && errMessages?.[0]) {
          const messageData = errMessages[0].message_text;

          const htmlMsg = reactStringReplace(messageData, '\n', () => <br />);

          await markAsRead({
            id: userDetails.user.id as string,
            message_id: [...errMessages.map((message) => message.id)]
          });
          pushErrorToast(<>{htmlMsg}</>);
        } else if (messages && messages?.[0]) {
          const messageData = messages[0].message_text;
          const messageEvent = messages[0].message_event;

          const userId = user?.id;

          if (UserAuth.isAuthenticated && userId)
            getUserDetails(userId as string);

          if (
            messageEvent !== MSG_EVENT.DASHBOARD_FILTER_SUCCESS &&
            messageEvent !== MSG_EVENT.DASHBOARD_FILTER_REMOVED &&
            messageEvent !== MSG_EVENT.DATA_RECALCULATION_COMPLETED
          ) {
            const htmlMsg = reactStringReplace(messageData, '\n', () => <br />);

            await markAsRead({
              id: userDetails.user.id as string,
              message_id: [messages?.[0]?.id as string]
            });

            if (messageEvent === MSG_EVENT.CLOUD_UPDATE_COMPLETED)
              pushInfoToast(<>{htmlMsg}</>);
            else pushSuccessToast(<>{htmlMsg}</>);
          }

          if (
            messageEvent === MSG_EVENT.FILE_COMBINE_SUCCESS ||
            messageEvent === MSG_EVENT.DATA_RECALCULATION_COMPLETED ||
            messageEvent === MSG_EVENT.CLOUD_UPDATE_COMPLETED ||
            messageEvent === MSG_EVENT.DASHBOARD_FILTER_SUCCESS ||
            messageEvent === MSG_EVENT.DASHBOARD_FILTER_REMOVED
          ) {
            if (projectBoard?.currentProjectData?.project_short_desc)
              setShowRedFlag(true);
            setRefetchChartConfigs(true);
            getProjectById({ projectId: projectData?.id });

            if (
              openConfigurator &&
              (messageEvent === MSG_EVENT.FILE_COMBINE_SUCCESS ||
                messageEvent === MSG_EVENT.DATA_RECALCULATION_COMPLETED ||
                messageEvent === MSG_EVENT.CLOUD_UPDATE_COMPLETED)
            )
              setOpenConfigurator(configuratorDispatch, false);
          } else {
            if (projectBoard?.currentProjectData?.project_short_desc)
              setShowRedFlag(true);
            setRefetchCharts(true);
          }
        }
      }
    };

    handleFilterNotification();
  }, [notifications.unreadMessages]);

  //hook to update projectVisualSettings into store
  useEffect(() => {
    if (updatedProjectData?.visual_settings)
      dispatch(setCurrentProjectData(updatedProjectData));
  }, [updatedProjectData]);

  useEffect(() => {
    if (projectData && projectData && projectData?.id === projectData?.id) {
      const newCharts = chartConfigs.reduce<string[] | undefined>(
        (newArr: string[] | undefined, chartConfig: ChartConfigType) => {
          if (!projectData?.chart_id_order?.includes(chartConfig.id))
            return [...(newArr || []), chartConfig.id];
        },
        []
      );

      setChartOrderArr(
        projectData?.chart_id_order
          ? [...projectData.chart_id_order, ...(newCharts || [])]
          : ['none']
      );
    }
  }, [projectData?.chart_id_order, chartConfigs.length]);

  useEffect(() => {
    if (isDataRefreshSuccess) {
      if (dataRefreshResponse === NO_DATA_MODIFICATIONS_AFTER_REFRESH)
        pushInfoToast(NO_DATA_MODIFICATIONS_AFTER_REFRESH);
      if (dataRefreshResponse === GSHEET_UPDATE_INITIATED)
        pushInfoToast(
          <>
            <span>Your data is being updated. Once complete,</span>
            <br />
            <span>1. A clean check will be run on your data again</span>
            <br />
            <span>
              2. Existing charts on your Dashboard will be updated with new data
            </span>
            <div>You will be notified once it is complete!</div>
          </>,
          { position: 'top-center' }
        );
    } else if (isDataRefreshError) {
      pushErrorToast(dataRefreshError.data);
    }
  }, [isDataRefreshSuccess, isDataRefreshError]);

  useEffect(() => {
    const handleFetchCharts = async () => {
      const shouldFetch = !isInitialLoad.current
        ? chartDataArr.length !== Object.keys(boardData || {})?.length
        : chartDataArr.length > 0;

      if (shouldFetch) {
        await fetchCharts(false);

        isInitialLoad.current = false;
      }
      setLoadedCharts(chartDataArr);
      if (chartOrderArr?.[0] === 'none')
        setChartOrderArr(chartConfigs.map((chartConfig) => chartConfig.id));
    };

    if (chartDataArr) handleFetchCharts();
  }, [chartDataArr]);

  //hook to refetch data after filter is applied
  useEffect(() => {
    if (isRefetchCharts) {
      fetchCharts(true);
      setRefetchCharts(false);
    }
  }, [isRefetchCharts]);

  //hook to refetch configs and data after successfull combine
  useEffect(() => {
    const refetchChartconfigs = async () => {
      if (isRefetchChartConfigs && projectData?.id) {
        await getChartConfigs({
          projectId: projectData?.id,
          collaboration_id: ''
        });
        getProjectById({ projectId: projectData?.id });
        setChartBoardLoader(false);
        setRefetchCharts(true);
        setRefetchChartConfigs(false);
      }
    };

    refetchChartconfigs();
  }, [isRefetchChartConfigs, projectData?.id]);

  useEffect(() => {
    setTimeout(() => {
      if (!openSqBChatWidget && !isBoardPreview) {
        const squirrelBotStatus = UserAuth.squirrelBotStatus;

        if (!squirrelBotStatus) {
          setOpenSqBChatWidget(true);
          UserAuth.setOpenedSquirrelBot('true');
        }
      }
    }, 5000);
    setOpenConfigurator(configuratorDispatch, false);
    if (!userModifiedSidenav) setMiniSidenav(configuratorDispatch, true);
  }, []);

  useEffect(
    () => () => {
      isInitialLoad.current = true;
      setOpenConfigurator(configuratorDispatch, false);
      am4core.disposeAllCharts();
      am4core.registry.deferred = [];
      am4core.registry.queue = [];
      setLoadedCharts([]);
      chartDataArr = [];
      dispatch(resetSqBotData());
    },
    []
  );

  // To get the width of the container where the charts are displayed and to update the same on window resize
  useEffect(() => {
    const parentContainer = parentContainerRef.current;

    if (parentContainer) {
      const updateParentContainerWidth = () => {
        const containerWidth = parentContainer.getBoundingClientRect().width;
        const marginWidth = isBoardPreview ? 7 * 9 : 7 * 8.2;
        const availableWidth = containerWidth - marginWidth;

        setUnitWidth(availableWidth / 6);
      };

      updateParentContainerWidth();

      // Update parent container width on window resize
      window.addEventListener('resize', updateParentContainerWidth);

      return () => {
        // Clean up the event listener on unmount
        window.removeEventListener('resize', updateParentContainerWidth);
      };
    }
  }, [openConfigurator]);

  //To update the width of chart container on side nav open and close
  useEffect(() => {
    setTimeout(() => {
      const parentContainer = parentContainerRef.current;

      if (parentContainer) {
        const containerWidth = parentContainer.getBoundingClientRect().width;
        const marginWidth = isBoardPreview ? 7 * 9 : 7 * 8.2;
        const availableWidth = containerWidth - marginWidth;

        setUnitWidth(availableWidth / 6);
      }
    }, 350);
  }, [miniSidenav]);

  useEffect(() => {
    if (
      projectData &&
      projectData?.project_status === ProjectStatus.ADDING_DATA
    ) {
      setIsAddDataModalOpen(true);
      setIsCombineReady(true);
    }
  }, [projectData]);

  // Hook to initiate polling of project if not yet started
  // and project is in COMBINING or CLEANING status
  useEffect(() => {
    if (
      isInitiatePolling ||
      (projectData &&
        projectData?.id &&
        [ProjectStatus.COMBINING, ProjectStatus.CLEANING].includes(
          projectData?.project_status as ProjectStatus
        ))
    ) {
      setIsCombineReady(false);
      setIsAddDataModalOpen(false);
      setChartBoardLoader(true);
      setInitiatePolling(false);
    }
  }, [projectData?.project_status, isInitiatePolling]);

  const onCreateChartComment = (commentDetails: any) => {
    if (projectData?.id && commentDetails)
      createComment({
        id: projectData?.id,
        payload: collaborationId
          ? { ...commentDetails, collaboration_id: collaborationId }
          : commentDetails
      });
  };

  const handleCloseSnackbar = () => setOpenSuccessSnackBar(false);

  const setDebouncedSearchText = useDebouncedCallback((text) => {
    setSearchText(text);
  }, 500);

  const syncUpdatedOrderWithBackend = useDebouncedCallback(
    (payload: string[]) => {
      dispatch(updateChartIdOrder(payload));
      updateOrderWithBackend({ id: projectData?.id, payload });
    },
    500
  );

  const handleCheckboxChange = (id: string) => {
    if (selectedChartIds?.includes(id))
      setSelectedChartIds((prevSelected) =>
        prevSelected.filter((chartId) => chartId !== id)
      );
    else setSelectedChartIds((prevSelected) => [...prevSelected, id]);
  };

  const handleDeleteChartById = (id: string) => {
    if (selectedChartIds?.includes(id))
      setSelectedChartIds((prevSelected) =>
        prevSelected.filter((chartId) => chartId !== id)
      );
  };

  const handleProjectThemeUpdate = async (colors: string[] | null) => {
    am4core.registry.deferred = [];
    am4core.registry.queue = [];
    await updateTheme({
      id: projectData?.id as string,
      payload: {
        colorset: colors ? colors?.join(',') : null
      }
    });
    setThemeChangedAfterSvgGeneration(true);
  };

  const onClickDownloadOrCancel = () => {
    setReportModalOpen((value) => !value);
    if (cleanedColumnsRedirect) setCleanedColumnsRedirect(false);
  };

  const handleCleanedDataDownloadOrCancel = () => {
    setCleanedColumnsRedirect((prev) => !prev);
    onClickDownloadOrCancel();
  };

  // function to call api to update project visual settings
  const handleVisualSettingsChange = (feature_name: string, value: boolean) => {
    if (
      !isBoardPreview &&
      chart3dToggle !== undefined &&
      gradientChartToggle !== undefined &&
      multiColorChartToggle !== undefined
    ) {
      am4core.registry.deferred = [];
      am4core.registry.queue = [];
      updateProject({
        id: projectData?.id as string,
        payload: {
          visual_settings: {
            ['3d_charts']: !!chart3dToggle,
            gradient_charts: !!gradientChartToggle,
            multicolor_charts: !!multiColorChartToggle,
            [feature_name]: value
          }
        }
      });
    }
  };

  const handlePollingEnd = () => {
    setpollingIntForNewChart(0);
    setStartPollingForSquirby(false);
    setRefetchChartConfigs(true);
  };

  const fetchCharts = async (force: boolean) => {
    if (!force) setChartDataLoading(true);

    for (const chartConfig of chartDataArr) {
      const {
        x_axis,
        y_axis,
        chart_operation,
        segment_field,
        filters,
        dynamic_filters,
        visual_settings
      } = chartConfig?.config || {};

      const dateGroup =
        chartConfig.config.date_group === 'none'
          ? chartConfig?.config?.x_axis?.treat_as_type === 'DATE'
            ? DATE_GROUP.DAYS
            : DATE_TIME_GROUP.HOURS
          : chartConfig.config.date_group;

      const currentFilter = getCurrentFilter({
        projectFilters:
          Object.keys(projectData?.filters || {})?.length > 0 ||
          Object.keys(projectData?.dynamic_filters || {})?.length > 0,
        toggleDashboardFilter:
          visual_settings?.toggleDashboardFilter as boolean,
        chartGlobalFilterToggle:
          visual_settings?.chartGlobalFilterToggle as boolean,
        chartFilters:
          Object.keys(filters || {})?.length > 0 ||
          Object.keys(dynamic_filters || {})?.length > 0
      });

      try {
        await getChartData({
          x_axis: x_axis?.id || '',
          y_axis: y_axis?.id || '',
          chart_operation,
          segment_field: segment_field?.id || null,
          id: chartConfig.id,
          date_group: dateGroup,
          force: force,
          empty_values_included: chartConfig.config?.visual_settings
            ?.showEmptyValues
            ? 'True'
            : 'False',
          auto_group_numbers: chartConfig.config?.visual_settings
            ?.autoGroupNumbers
            ? 'True'
            : 'False',
          is_kpi: chartConfig.config.chart_type === 'KPI' ? 'True' : 'False',
          chart_type: chartConfig.config.chart_type,
          percentage_format: chartConfig.config?.visual_settings
            ?.showAsPercentage
            ? 'True'
            : 'False',
          collaboration_id: collaborationId,
          ...(currentFilter !== 'NONE' && {
            filters:
              currentFilter === 'PROJECT' ? projectData?.filters : filters,
            dynamic_filters:
              currentFilter === 'PROJECT'
                ? projectData?.dynamic_filters
                : dynamic_filters
          })
        });
      } catch (err) {
        console.log(err);
      }
    }

    setChartDataLoading(false);
  };

  const onCloseConfigurator = () => {
    setOpenConfigurator(configuratorDispatch, false);
    setTimeout(() => {
      setConfiguratorFlag(0);
    }, 400);
  };

  const onOpenSlideIn = useDebouncedCallback(
    (active: number, openConfigurator: boolean) => {
      if (configuratorFlag === 0 || !openConfigurator) {
        setConfiguratorFlag(active);
        setOpenConfigurator(configuratorDispatch, true);
      } else {
        if (openConfigurator)
          if (configuratorFlag === active) {
            if (isBoardPreview) {
              setOpenConfigurator(configuratorDispatch, false);
              setTimeout(() => {
                setConfiguratorFlag(0);
              }, 400);
            }
          } else {
            if (isBoardPreview) {
              setOpenConfigurator(configuratorDispatch, false);
              setTimeout(() => {
                setConfiguratorFlag(active);
                setOpenConfigurator(configuratorDispatch, true);
              }, 300);
            } else {
              setConfiguratorFlag(active);
            }
          }
      }
    },
    300
  );

  const onChange = (chartIds: any[]) => {
    // Check if length or chart id array from Murri is same as actual
    // else reset order
    // Fail safe for Murri failure

    let formattedChartIds = chartIds?.map((str) =>
      str?.replace(/(true|false)$/, '')
    );

    if (chartIds?.length !== chartConfigs?.length)
      formattedChartIds = chartConfigs?.map((chart) => chart?.id);

    const reorderedCharts = formattedChartIds.map((id) =>
      chartConfigs.find((chart) => chart.id === id)
    );

    setLoadedCharts(reorderedCharts as ChartConfigType[]);

    if (chartOrderArr && formattedChartIds?.join() !== chartOrderArr?.join()) {
      setChartOrderArr(formattedChartIds);

      syncUpdatedOrderWithBackend(formattedChartIds);
    }
  };

  const handleSearchTextKeyDown = () => {
    if (isBoardPreview) return;
    if (!chartDataArr.length) {
      setSendInitText(true);
      setOpenSqBChatWidget(true);
    }
  };

  const onClickRemoveWaterMark = () => {
    sendAmplitudeData(CLICKED_REMOVE_WATERMARK, {
      location_type: 'Chart'
    });
    dispatch(
      setPlanUpdateErrors({
        button_text: 'Choose a plan',
        feature_name: 'Remove watermark',
        modal_name: 'Feature Limit',
        title: `Access this feature with an upgrade to your current ${getPlanName(
          userDetails.plan,
          userDetails.is_trial
        )} plan.`,
        sub_title:
          'Upgrade your plan to enjoy using advanced features. Simply click below and choose from a wide range of plans that suit your needs.'
      })
    );
  };

  const handleRefreshData = async () => {
    if (subscriptionFlag) {
      // If feature not accessible by plan then return
      const feature = isUpdateDataEnabledByPlan
        ? FeatureTypes.MAX_MONTHLY_DATA_UPDATE_LIMIT
        : FeatureTypes.MANUAL_UPDATE;

      const response = await getFeatureAccess(feature);

      if (response?.isSuccess) setOpenRefreshDataModal(true);
    }
  };

  const handleRefreshDataModalClose = () => {
    setOpenRefreshDataModal(false);
  };

  const handleRefreshClick = () => {
    sendAmplitudeData(CONFIRM_REFRESH_DATA);
    handleRefreshDataModalClose();
    refreshData({ project_id: projectId });
  };

  return (
    <>
      <MainLayout
        sectionTitle='DASHBOARD'
        pageTitle={
          chartConfigs?.length
            ? chartConfigs.length > 1
              ? `${chartConfigs?.length} Charts`
              : `${chartConfigs?.length} Chart`
            : ''
        }
        sectionTooltip='This steps helps you create charts, dashboard views, reports - comment, download or share them!'
        titleTooltip='Total number of charts in this Project'
        isPreview={isBoardPreview}
        toggleConfigurator={(type: number) => {
          onOpenSlideIn(type, openConfigurator);
        }}
        padding='10px 15px 15px 8px'
        showUploadLimitMessage
      >
        {chartBoardLoader && (
          <div style={{ position: 'relative' }}>
            <Backdrop
              sx={{
                height: '100vh',
                width: '100%',
                position: 'absolute',
                background: 'rgba(0,0,0,0)',
                zIndex: isAddDataModalOpen ? 500 : 50
              }}
              open={true}
              onClick={() => null}
            >
              <FullPageLoader />
            </Backdrop>
          </div>
        )}
        <ProjectHeader
          searchText={searchText}
          setSearchText={setDebouncedSearchText}
          selectedChartIds={selectedChartIds}
          setSelectedChartIds={setSelectedChartIds}
          deleteSelected={() => {
            deleteMultipleCharts(selectedChartIds);
            setSelectedChartIds([]);
            sendAmplitudeData(DELETE_MULTI_CHART, {
              'Pre-delete chart count': chartDataArr.length,
              'Post-delete chart count':
                chartDataArr.length - selectedChartIds.length
            });
          }}
          chartConfigs={
            chartConfigs.length === sortedChartConfigs.length
              ? chartDataArr
              : undefined
          }
          filterTooltip={getFilterTooltipText(
            projectData?.filters || {},
            projectData?.dynamic_filters || {},
            dropDownData?.available_fields,
            false,
            '',
            projectData?.date_range
          )}
          calenderTooltip={getFilterTooltipText(
            projectData?.filters || {},
            projectData?.dynamic_filters || {},
            dropDownData?.available_fields,
            true,
            '',
            projectData?.date_range
          )}
          toggleConfigurator={(type: number) => {
            onOpenSlideIn(type, openConfigurator);
          }}
          isBoardPreview={isBoardPreview}
          onDownloadClick={() => {
            sendAmplitudeData(CLICKED_DOWNLOAD);
            setReportModalOpen(true);
          }}
          onInsightsClick={async () => {
            sendAmplitudeData(CLICKED_SHOW_INSIGHT, {
              type: 'Project',
              red_flag: showRedFlag
            });
            if (subscriptionFlag) {
              // If feature not accessible by plan then return

              const feature = isProjectInsightsEnabledByPlan
                ? FeatureTypes.MAX_MONTHLY_INSIGHTS_LIMIT
                : FeatureTypes.PROJECT_INSIGHTS;
              const response = await getFeatureAccess(feature);

              if (response?.isSuccess) setOpenInsightsModal(true);
            }
          }}
          onAddData={async () => {
            sendAmplitudeData(CLICKED_ADD_DATA);
            if (subscriptionFlag) {
              // If feature not accessible by plan then return
              const response = await getFeatureAccess(
                FeatureTypes.COMBINE_APPEND_FILE
              );

              if (response?.isSuccess) setIsAddDataModalOpen(true);
            }
          }}
          isGoAutoEnabled={
            chartConfigs.filter(
              (chartData: any) => chartData.created_by_auto_charts === true
            ).length > 0
              ? false
              : true
          }
          setOpenCleanActionModal={setOpenCleanActionsModal}
          handleOpenSlider={(tabIndex: number) =>
            onOpenSlideIn(tabIndex, openConfigurator)
          }
          handleSearchTextKeyDown={handleSearchTextKeyDown}
          onRefreshData={handleRefreshData}
          showRedFlag={showRedFlag}
          setShowRedFlag={setShowRedFlag}
        />
        {openConfigurator && (
          <MDBox
            position={{ xs: 'fixed', sm: 'sticky' }}
            top={{ xs: '0px', sm: '32px' }}
            right={0}
            zIndex={{ xs: 12, sm: 60 }}
          >
            <Configurator
              width='360px'
              configuratorFlag={configuratorFlag}
              onCloseConfigurator={() => onCloseConfigurator()}
              setConfiguratorFlag={setConfiguratorFlag}
              isProjectBoard={isBoardPreview ? false : true}
            >
              <Grid
                height={{ xs: '100vh', sm: isBoardPreview ? '60vh' : '62vh' }}
                width='100%'
              >
                <If condition={configuratorFlag === 0}>
                  <Grid
                    container
                    flexDirection='column'
                    px={1}
                    minHeight={{ xs: '100vh', sm: '600px' }}
                  >
                    <Grid container alignItems={'center'} width='100%'>
                      <Grid container alignItems={'center'} width='95%'>
                        <>
                          <Grid
                            container
                            alignItems={'center'}
                            height='36px'
                            width='100%'
                          >
                            <MDBox
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mt: 1,
                                mb: 2
                              }}
                            >
                              <Switch
                                sx={{
                                  width: '2.4rem',
                                  height: '1.15rem',
                                  '& .MuiSwitch-thumb': {
                                    width: '.9rem',
                                    height: '.9rem'
                                  }
                                }}
                                checked={chart3dToggle}
                                onChange={(_e, value) => {
                                  set3dChartToggle(value);
                                  handleVisualSettingsChange(
                                    '3d_charts',
                                    value
                                  );
                                  if (value) sendAmplitudeData(SET_3D);
                                }}
                              />
                              <MDTypography
                                variant='sm'
                                fontWeight='regular'
                                mt={0.2}
                                color='info'
                                ml={1}
                                opacity={1}
                              >
                                Show Charts as 3D
                              </MDTypography>
                            </MDBox>
                            <MDBox
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 1
                              }}
                            >
                              <Switch
                                sx={{
                                  width: '2.4rem',
                                  height: '1.15rem',
                                  '& .MuiSwitch-thumb': {
                                    width: '.9rem',
                                    height: '.9rem'
                                  }
                                }}
                                checked={gradientChartToggle}
                                onChange={(_e, value) => {
                                  setGradientChartToggle(value);
                                  handleVisualSettingsChange(
                                    'gradient_charts',
                                    value
                                  );
                                  if (value) sendAmplitudeData(SET_GRADIENT);
                                }}
                              />
                              <MDTypography
                                variant='sm'
                                fontWeight='regular'
                                mt={0.2}
                                color='info'
                                ml={1}
                              >
                                Show Charts as Gradient
                              </MDTypography>
                            </MDBox>
                            <MDBox
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 1
                              }}
                            >
                              <Tooltip
                                title={
                                  segmentedChartIndex > -1
                                    ? 'Sorry, this option is unavailable because some of the charts contain segments. Enabling this option may make it difficult to read your charts.'
                                    : ''
                                }
                                placement='top'
                                sx={{ pointerEvents: 'auto' }}
                              >
                                <div>
                                  <Switch
                                    sx={{
                                      width: '2.4rem',
                                      height: '1.15rem',
                                      '& .MuiSwitch-thumb': {
                                        width: '.9rem',
                                        height: '.9rem'
                                      }
                                    }}
                                    checked={
                                      segmentedChartIndex === -1 &&
                                      multiColorChartToggle
                                    }
                                    onChange={(_e, value) => {
                                      setMultiColorChartToggle(value);
                                      handleVisualSettingsChange(
                                        'multicolor_charts',
                                        value
                                      );
                                      if (value)
                                        sendAmplitudeData(SET_MULTI_COLOUR);
                                    }}
                                    disabled={segmentedChartIndex > -1}
                                  />
                                </div>
                              </Tooltip>
                              <MDTypography
                                variant='sm'
                                fontWeight='regular'
                                mt={0.2}
                                color='info'
                                ml={1}
                                opacity={segmentedChartIndex === -1 ? 1 : 0.5}
                              >
                                Show Multi-colour Charts
                              </MDTypography>
                            </MDBox>
                            <Grid container item xs={11} alignItems={'center'}>
                              <Tooltip title={themeTooltip} placement='top'>
                                <Switch
                                  sx={{
                                    width: '2.4rem',
                                    height: '1.15rem',
                                    '& .MuiSwitch-thumb': {
                                      width: '.9rem',
                                      height: '.9rem'
                                    }
                                  }}
                                  checked={selectThemeToggle}
                                  onChange={(e, value) => {
                                    setThemeTooltip(
                                      value === true
                                        ? 'Click to remove theme'
                                        : 'Click to add a theme'
                                    );
                                    setShowThemeMenu(value);
                                    setSelectThemeToggle(value);
                                    if (!value) handleProjectThemeUpdate(null);
                                  }}
                                />
                              </Tooltip>
                              <MDTypography
                                variant='sm'
                                fontWeight='regular'
                                mt={0.2}
                                color='info'
                                ml={1}
                              >
                                Select a Color Palette
                              </MDTypography>
                            </Grid>
                            {selectThemeToggle && (
                              <>
                                <Grid item xs={1}>
                                  {preventClosing ? (
                                    <CircularProgress
                                      color='secondary'
                                      size='20px'
                                    />
                                  ) : (
                                    <Tooltip
                                      title={
                                        showThemeMenu === true
                                          ? 'Click to close Theme menu'
                                          : ''
                                      }
                                      placement='top'
                                    >
                                      <IconButton
                                        onClick={() =>
                                          setShowThemeMenu((prev) => !prev)
                                        }
                                      >
                                        <FaIcon
                                          icon={'chevron-down'}
                                          typographyProps={{
                                            color: 'info',
                                            variant: 'xl'
                                          }}
                                          style={{
                                            transition: 'all .3s ease-in-out',
                                            transform: `rotate(${
                                              showThemeMenu ? '180deg' : 0
                                            })`
                                          }}
                                        />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Grid>
                                <MDBox width='100%'>
                                  {showThemeMenu && selectThemeToggle && (
                                    <SelectThemeMenu
                                      isProjectBoard
                                      setSelectedTheme={(
                                        colors: string[] | null
                                      ) => {
                                        handleProjectThemeUpdate(colors);
                                      }}
                                    />
                                  )}
                                </MDBox>
                              </>
                            )}
                          </Grid>
                        </>
                      </Grid>
                    </Grid>
                  </Grid>
                </If>
                <If condition={configuratorFlag === 1}>
                  <Grid container minHeight={{ xs: '100vh', sm: '600px' }}>
                    <PBFilter
                      filters={projectData?.filters || {}}
                      dynamic_filters={projectData?.dynamic_filters || {}}
                    />
                  </Grid>
                </If>
                <If condition={configuratorFlag === 2}>
                  <Grid container minHeight={{ xs: '100vh', sm: '600px' }}>
                    <CommentsList
                      commentsList={commentsList}
                      onPostComment={onCreateChartComment}
                      height={600}
                      isEditChartOrPB={isBoardPreview ? false : true}
                    />
                  </Grid>
                </If>
              </Grid>
            </Configurator>
          </MDBox>
        )}

        {(Object.keys(projectData?.filters || {}).length > 0 ||
          Object.keys(projectData?.dynamic_filters || {}).length > 0) &&
          !isBoardPreview && (
            <Grid
              container
              mb={2}
              onClick={() => onOpenSlideIn(1, openConfigurator)}
              width={{
                xs: '100%',
                sm: openConfigurator ? 'calc(100% - 365px)' : '100%'
              }}
            >
              <MDBox
                sx={{
                  display: '-webkit-box',
                  overflow: 'hidden',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  cursor: 'pointer',
                  lineHeight: '5px'
                }}
              >
                <Tooltip
                  title={
                    showTooltip
                      ? getFilterTooltipText(
                          projectData?.filters || {},
                          projectData?.dynamic_filters || {},
                          dropDownData?.available_fields,
                          false,
                          '',
                          projectData?.date_range
                        )
                      : ''
                  }
                  placement='top'
                >
                  <MDTypography
                    ref={typographyRef}
                    variant='sm'
                    color='info'
                    sx={{
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {getFilterTooltipText(
                      projectData?.filters || {},
                      projectData?.dynamic_filters || {},
                      dropDownData?.available_fields,
                      false,
                      '',
                      projectData?.date_range
                    )}
                  </MDTypography>
                </Tooltip>
              </MDBox>
            </Grid>
          )}

        <Grid container position='relative' id={pbWithChartsId}>
          <Grid
            container
            item
            position='relative'
            direction={'column'}
            alignItems='center'
            width={{
              xs: '100%',
              sm: openConfigurator
                ? 'calc(100% - 360px)'
                : isBoardPreview
                ? '100%'
                : '100%'
            }}
            mb={2}
            ref={parentContainerRef}
          >
            <MDBox opacity={isMultipleDeleteStarted ? 0 : 1} width='100%'>
              {chartDataArr && (
                <>
                  <If condition={!mobileView}>
                    {isBoardPreview ? (
                      <MuuriComponent>
                        {!chartDataLoading
                          ? loadedCharts.map((chart) => (
                              <ProjectBoardCharts
                                key={chart.id}
                                width={
                                  chart.config.visual_settings?.size?.w
                                    ? unitWidth *
                                      chart.config.visual_settings?.size.w
                                    : chart.config.chart_type === 'KPI' &&
                                      !chart.config.visual_settings?.kpiTarget
                                    ? unitWidth * 1
                                    : unitWidth * 2
                                }
                                height={
                                  chart.config.visual_settings?.size?.h
                                    ? unitHeight *
                                      chart.config.visual_settings?.size?.h
                                    : chart.config.chart_type === 'KPI' &&
                                      !chart.config.visual_settings?.kpiTarget
                                    ? unitHeight * 1
                                    : unitHeight * 2
                                }
                                configuratorFlag={openConfigurator}
                              >
                                {showItem(chart)}
                              </ProjectBoardCharts>
                            ))
                          : []}
                      </MuuriComponent>
                    ) : (
                      <MuuriComponent
                        dragEnabled={searchText ? false : true}
                        dragHandle={'.chart-card'}
                        dragStartPredicate={{
                          delay: 200
                        }}
                        onDragStart={() => {
                          sendAmplitudeData(INITIATED_CHART_REPOSITION);
                        }}
                        onDragEnd={(item) => {
                          const grid = item.getGrid();
                          const items = grid.getItems();
                          const keys = items.map((item) => item.getKey());
                          //To send amplitude data
                          const currentItemKey = String(item.getKey()).replace(
                            /(true|false)$/,
                            ''
                          );

                          const oldIndex = chartOrderArr?.findIndex(
                            (value) => value === currentItemKey
                          );
                          const newIndex = keys.findIndex(
                            (value) => value === item.getKey()
                          );

                          if (oldIndex !== newIndex) {
                            sendAmplitudeData(REPOSITION_CHART, {
                              'Position (before)':
                                oldIndex !== undefined && oldIndex !== null
                                  ? oldIndex + 1
                                  : oldIndex,
                              'Position (after)':
                                newIndex !== undefined && newIndex !== null
                                  ? newIndex + 1
                                  : newIndex
                            });
                            onChange(keys);
                          }
                        }}
                      >
                        {!chartDataLoading
                          ? loadedCharts.map((chart) => {
                              const updateValue = forceUpdate.find(
                                (obj) => obj.id === chart.id
                              );

                              const key =
                                `${chart.id}` + `${updateValue.update}`;

                              return (
                                <ResizableWrapper
                                  height={
                                    chart.config.visual_settings?.size?.h
                                      ? unitHeight *
                                        chart.config.visual_settings?.size?.h
                                      : chart.config.chart_type === 'KPI' &&
                                        !chart.config.visual_settings?.kpiTarget
                                      ? unitHeight * 1
                                      : unitHeight * 2
                                  }
                                  key={key}
                                  width={
                                    chart.config.visual_settings?.size?.w
                                      ? unitWidth *
                                        chart.config.visual_settings?.size.w
                                      : chart.config.chart_type === 'KPI' &&
                                        !chart.config.visual_settings?.kpiTarget
                                      ? unitWidth * 1
                                      : unitWidth * 2
                                  }
                                  minConstraints={[unitWidth, unitHeight]}
                                  maxConstraints={[
                                    unitWidth * 6,
                                    unitHeight * 3
                                  ]}
                                  unitWidth={unitWidth}
                                  unitHeight={unitHeight}
                                  visualSettings={chart.config.visual_settings}
                                  chartId={chart.id}
                                  openConfigurator={openConfigurator}
                                  miniSidenav={miniSidenav}
                                  charts={loadedCharts}
                                  setCharts={setLoadedCharts}
                                  chartConfigs={chartConfigs}
                                  setForceUpdate={setForceUpdate}
                                  forceUpdate={forceUpdate}
                                  isKpi={
                                    chart.config.chart_type === 'KPI' &&
                                    !chart.config.visual_settings?.kpiTarget
                                      ? true
                                      : false
                                  }
                                >
                                  {showItem(chart)}
                                </ResizableWrapper>
                              );
                            })
                          : []}
                      </MuuriComponent>
                    )}
                  </If>

                  <If condition={mobileView}>
                    <Grid item display='flex' flexDirection='column' rowGap={2}>
                      {!chartDataLoading
                        ? loadedCharts.map((chart) => (
                            <MDBox key={chart.id}>{showItem(chart)}</MDBox>
                          ))
                        : []}
                    </Grid>
                  </If>
                </>
              )}
            </MDBox>
            {chartDataArr.length === 0 &&
              isBoardPreview &&
              !searchText.length && (
                <Grid
                  container
                  display='flex'
                  flex='1'
                  alignContent='center'
                  justifyContent='center'
                  marginTop='10%'
                >
                  Zero charts created for this data.
                </Grid>
              )}
            {(isMultipleDeleteStarted || chartDataLoading) &&
              !isAddDataModalOpen && (
                <MDBox position='fixed' top={50} height='100vh' zIndex={99}>
                  <FullPageLoader height='100%' />
                </MDBox>
              )}
          </Grid>
        </Grid>
      </MainLayout>
      {reportModalOpen && (
        <ReportGenerationModal
          open={reportModalOpen}
          disableDownloadReport={false}
          onClose={(charts?: string[]) => {
            setTimeout(() => {
              if (charts) {
                setSvgGeneratedCharts(charts);
                setThemeChangedAfterSvgGeneration(false);
              }
            }, 600);
            setReportModalOpen(false);
            if (cleanedColumnsRedirect) setCleanedColumnsRedirect(false);
          }}
          chartConfigs={
            themeChangedAfterSvgGeneration
              ? chartConfigs.map((config: ChartConfigType) => ({
                  ...config,
                  svg_generated: false
                }))
              : chartConfigs
          }
          selectedChartIds={selectedChartIds}
          themeData={projectData?.colorset}
          svgGeneratedCharts={
            themeChangedAfterSvgGeneration ? [] : svgGeneratedCharts
          }
          cleanedColumnsRedirect={cleanedColumnsRedirect}
        />
      )}
      {isBoardPreview && (
        <Snackbar
          open={openSuccessSnackBar}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
        >
          <Alert
            severity='success'
            sx={{ width: '100%' }}
            onClose={handleCloseSnackbar}
          >
            You can view this Dashboard and leave comments below.
          </Alert>
        </Snackbar>
      )}
      {openCleanActionsModal && (
        <CleanActionsModal
          open={openCleanActionsModal}
          onClose={() => setOpenCleanActionsModal(false)}
          setDownloadClicked={handleCleanedDataDownloadOrCancel}
        />
      )}
      {isAddDataModalOpen && (
        <FileUploadModal
          open={isAddDataModalOpen}
          onClose={() => setIsAddDataModalOpen(false)}
          initFileType={
            projectData?.is_api_connected
              ? 'API'
              : getFileType(projectData?.project_datafiles || [])
          }
          isAddData
          isCombineReady={isCombineReady}
          projectDataFiles={projectData?.project_datafiles}
          setIsCombineReady={setIsCombineReady}
          setInitiatePolling={setInitiatePolling}
        />
      )}
      {openSqBChatWidget && (
        <SqBChatWidget
          open={openSqBChatWidget}
          onClose={() => {
            setOpenSqBChatWidget(false);
          }}
          projectId={projectData?.id || ''}
          setRefetchChartConfigs={setRefetchChartConfigs}
          initText={sendInitText ? searchText : ''}
          setStartPollingForSquirby={setStartPollingForSquirby}
        />
      )}
      {openInsightsModal && (
        <InsightsModal
          open={openInsightsModal}
          onClose={() => setOpenInsightsModal(false)}
          insights={
            typeof projectData?.project_insights === 'string' &&
            projectData?.project_insights !== '' &&
            projectData?.project_insights !== INSIGHT_CREATION_FAILED
              ? projectData?.project_insights
              : '[]'
          }
          projectId={projectData?.id || ''}
          currentDesc={projectData?.project_short_desc as string}
        />
      )}
      {!isBoardPreview && (
        <Tooltip title=''>
          <MDBox
            sx={({ palette }) => ({
              background: linearGradient(
                palette.gradients.secondary.main,
                palette.gradients.secondary.state
              ),
              position: 'fixed',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bottom: 65,
              right: 15,
              borderRadius: 50,
              boxShadow: '0 3px 10px rgb(0 0 0 / 0.2)',
              cursor: 'pointer',
              zIndex: 100,
              padding: '5px'
            })}
            onClick={() => {
              setOpenSqBChatWidget(true);
            }}
          >
            <MDBox
              component='img'
              src={brandLogo}
              alt='Brand'
              sx={{
                height: 55,
                width: 55,
                padding: '11px'
              }}
            />
          </MDBox>
        </Tooltip>
      )}

      {openRefreshDataModal && (
        <RefreshDataModal
          sheetName={projectData?.project_name ?? ''}
          onClose={handleRefreshDataModalClose}
          lastUpdatedTimestamp={getUTCTime(
            projectBoard?.currentProjectData?.project_data_updated_at as string
          )}
          planName={userDetails.plan}
          handleRefreshClick={handleRefreshClick}
        />
      )}

      <Snackbar
        open={!!infoSnackBarMessage}
        autoHideDuration={3000}
        onClose={() => setInfoSnackBarMessage('')}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
      >
        <Grid container sx={{ flex: 1, justifyContent: 'center' }}>
          <Alert
            severity='info'
            sx={{ display: 'flex', alignItems: 'center', marginTop: '10px' }}
            onClose={() => setInfoSnackBarMessage('')}
          >
            {infoSnackBarMessage}
          </Alert>
        </Grid>
      </Snackbar>
    </>
  );
};

export default ChartBoard;
